import {
  Box,
  Button,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { PayloadAction } from '@reduxjs/toolkit';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getFile } from '../../redux/actions/fileActions';
import { AppDispatch } from '../../redux/store';
import { CustomFile } from '../../Types/file';
import { LlmEnum, llms } from '../../Types/llm';

const IMAGE_MIME_TYPE_ALLOWLIST = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
];

interface FileViewDialogProps {
  open: boolean;
  onClose: () => void;
  file: CustomFile;
  handleTextChange?: (newText: string) => void;
  llm: LlmEnum;
  isPrivate?: boolean;
  isViewOnly?: boolean;
}

const FileViewDialog = ({
  open,
  onClose,
  file,
  handleTextChange,
  llm,
  isPrivate = false,
  isViewOnly = false,
}: FileViewDialogProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [currentFile, setCurrentFile] = useState<CustomFile | undefined>(file);
  const [localText, setLocalText] = useState(file.contents ? file.contents : '');
  const [isImage, setIsImage] = useState(file && IMAGE_MIME_TYPE_ALLOWLIST.includes(file.mimetype));
  const [sendAsImage, setSendAsImage] = useState(
    IMAGE_MIME_TYPE_ALLOWLIST.includes(file.mimetype) && llms[llm as LlmEnum]?.imagesAllowed
  );

  const handleSave = () => {
    if (handleTextChange) {
      handleTextChange(localText);
      onClose();
    }
  };

  const handleCancel = () => {
    setLocalText(file.contents ? file.contents : '');
    setCurrentFile(undefined);
    onClose();
  };

  const fetchFileContents = useCallback(async () => {
    dispatch(getFile(file.id)).then((action: PayloadAction<CustomFile | unknown>) => {
      const retFile = action.payload as CustomFile;
      setCurrentFile(retFile);
      setLocalText(retFile.contents ?? '');
    });
  }, [dispatch, file.id]);

  useEffect(() => {
    setIsImage(IMAGE_MIME_TYPE_ALLOWLIST.includes(file.mimetype));
    setSendAsImage(
      IMAGE_MIME_TYPE_ALLOWLIST.includes(file.mimetype) && llms[llm as LlmEnum]?.imagesAllowed
    );
    setLocalText(file.contents ? file.contents : '');
    if (!isPrivate) {
      fetchFileContents();
    }
  }, [file, fetchFileContents, llm, isPrivate]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      aria-labelledby="file-view-dialog-title"
    >
      <DialogTitle id="file-view-dialog-title">
        <Box display="flex" alignItems="center">
          {currentFile?.fileName}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Show Loader */}
        {((isImage && currentFile?.encoding === undefined) || (!isImage && localText === '')) && (
          <CardContent
            sx={{
              paddingTop: '0px',
              paddingBottom: '17px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <CircularProgress />
          </CardContent>
        )}

        {/* Show Image */}
        {sendAsImage && (
          <CardContent
            sx={{
              paddingTop: '0px',
              paddingBottom: '10px',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <img
              src={`data:image/png;base64, ${currentFile?.encoding}`}
              alt={currentFile?.fileName}
              style={{ maxHeight: '300px', maxWidth: '100%' }}
            />
          </CardContent>
        )}

        {/* Empty File */}
        {!sendAsImage && localText === '' && (
          <Typography
            variant="caption"
            component="p"
            fontSize="small"
            style={{ padding: '0 16px 10px' }}
          >
            No text was found in your file
          </Typography>
        )}

        {/* Show Text */}
        {(!isImage || (!sendAsImage && currentFile?.contents)) &&
          (handleTextChange ? (
            <CardContent
              sx={{
                paddingTop: '0px',
                paddingBottom: '17px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              {isViewOnly ? (
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {localText}
                </Typography>
              ) : (
                <Box>
                  <TextField
                    value={localText}
                    onChange={(event) => setLocalText(event.target.value)}
                    sx={{
                      whiteSpace: 'pre-wrap',
                    }}
                    multiline
                    fullWidth
                  />
                  <Box display="flex" gap={2} mt={1}>
                    <Button variant="contained" sx={{ width: '10px' }} onClick={handleSave}>
                      Save
                    </Button>
                    <Button variant="text" sx={{ width: '10px' }} onClick={handleCancel}>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          ) : (
            <CardContent sx={{ paddingTop: '0px', paddingBottom: '10px' }}>
              <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                {localText}
              </Typography>
            </CardContent>
          ))}
        {/* Show Azure Citations */}
        {/* {azureCitations && (
          <CardContent sx={{ paddingTop: '0px', paddingBottom: '10px' }}>
            <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {azureCitations.content}
            </Typography>
          </CardContent>
        )} */}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileViewDialog;
